import { throttle } from "lodash-es";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";

import { Artefact } from "@/lib/artefacts";
import { getDescriptionArtefact } from "@/lib/artefacts/markdown";
import { api, type types } from "@/lib/openapi";
import { withThreadRevision } from "@/lib/thread-revision-link";
import router from "@/router";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { defineEntityStore } from "@/stores/helpers";
import { useReleaseStore } from "@/stores/release-store";
import { useSyncStore } from "@/stores/sync-store";

import {
    FILE_SYNC_THROTTLE_MS,
    discardDraftsAfterSync,
    useDraftManager,
} from "../composables/draft-manager";
import { useEditManager } from "../composables/edit-manager";
import { registerFlush } from "../composables/pending-flush";
import {
    refreshOnCrossTabEntitySync,
    wouldClobberLocalChanges,
} from "../composables/refresh-on-entity-change";
import { clearStaleContentOnSwitch } from "../composables/stale-while-revalidate";
import {
    isAbortError,
    useLatestRequest,
} from "../composables/use-latest-request";

export type Flow = types.ProcessFlow;

// Uses the current route to determine what the 'flow' should be and fetches
// the flow description for you.
export const useFlowStore = defineEntityStore("flow", () => {
    const flow = ref<Flow>();
    const syncStore = useSyncStore();
    const entityId = computed(() => flow.value?.identity);
    const description = ref<Artefact<"text/markdown">>();
    const descriptionDraft = useDraftManager({
        entityId,
        entityType: EntityType.FLOW,
        attribute: "description",
        artefact: description,
    });
    discardDraftsAfterSync(entityId, [descriptionDraft], EntityType.FLOW);

    const {
        organization,
        stek,
        process,
        flow: flowInfo,
    } = useCurrentEntityManager();
    const releaseStore = useReleaseStore();
    const revisionQuery = computed(() =>
        releaseStore.activeRevision
            ? new URLSearchParams({ revision: releaseStore.activeRevision })
            : undefined
    );
    const isFetching = ref(false);
    // Latest-request guard: each updateFlow gets its own AbortController +
    // token, so a stale run can neither abort the current one nor commit results.
    const { begin, isCurrent } = useLatestRequest();

    async function updateFlow(force = false) {
        const flowId = flowInfo.value?.identity;

        // In case the same flow is selected, return early.
        if (!force && flow.value && flow.value.identity === flowId) {
            return;
        }

        // Start a new request: aborts the previous one and gives us our own
        // signal + token. See useLatestRequest.
        const { token, signal } = begin();

        if (!flowId) {
            description.value = undefined;
            flow.value = undefined;
            return;
        }

        isFetching.value = true;
        // Stale-while-revalidate: keep the previously-loaded flow + description
        // on screen while the *same* flow is refetched, so it doesn't blank the
        // page (the deferred spinner then never flashes). The data overwrites
        // below; updateDescription always refetches for the current flow.
        // Switching to a *different* element is handled by clearStaleContentOnSwitch.

        try {
            const { response, data } =
                await api.startProcessProcessElementMgtGetFlowStartPost({
                    body: {
                        flow_id: `${flowId}`,
                        include_archived: true,
                        revision: releaseStore.activeRevision ?? undefined,
                    },
                    signal,
                });

            // A newer request superseded us while fetching — commit nothing.
            if (!isCurrent(token)) {
                return;
            }

            if (!response.ok) {
                if (response.status === 404) {
                    if (!releaseStore.activeRevision) {
                        router.replace({
                            name: "not-found",
                            params: {
                                orgName: organization.value?.name,
                                stekName: stek.value?.name,
                            },
                            query: {
                                type: "flow",
                                id: flowId,
                            },
                        });
                    }
                    return;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Landing-time clobber guard: edits may have appeared while this
            // GET was in flight — the notification-time guard can't see
            // them. Only a same-flow refetch is dropped; navigating to a
            // different flow must always commit. A dropped commit
            // self-heals: the edit's own sync re-notifies and refetches.
            if (
                flowId === flow.value?.identity &&
                wouldClobberLocalChanges(externalRefresh)
            ) {
                return;
            }
            flow.value = data;

            if (flow.value) {
                await updateDescription(signal, token);
            }
        } catch (e) {
            if (isAbortError(e)) {
                return;
            }
            throw e;
        } finally {
            if (isCurrent(token)) {
                isFetching.value = false;
            }
        }
    }
    // Drop the previous element's content when the route switches to a different
    // element, so the new element's page never briefly shows the old one.
    clearStaleContentOnSwitch(
        () => flowInfo.value?.identity,
        [flow, description]
    );
    watch(
        () => flowInfo.value?.identity,
        () => updateFlow(),
        { immediate: true }
    );
    watch(
        () => releaseStore.activeRevision,
        () => {
            if (flow.value) updateFlow(true);
        }
    );

    const updateDescription = async (signal: AbortSignal, token: number) => {
        if (!flow.value) {
            return;
        }

        // Always refetch for the (possibly new) current flow — under
        // stale-while-revalidate the previous description stays set, so an
        // `if (!description.value)` guard would wrongly keep the stale value.
        try {
            const descriptionResult = await getDescriptionArtefact(
                flow.value,
                "flowDescription",
                signal,
                revisionQuery.value
            );

            // A newer request superseded us — commit nothing. Same for
            // edits that appeared while fetching (landing-time guard).
            if (
                !isCurrent(token) ||
                wouldClobberLocalChanges(externalRefresh)
            ) {
                return;
            }

            description.value = descriptionResult;
        } catch (e) {
            if (isAbortError(e)) {
                return;
            }
            throw e;
        }
    };

    // No isFetching short-circuit here: edits made WHILE a refetch is in
    // flight must read as modified, or the landing-time clobber guard can't
    // see them and the refetch's response overwrites the user's text.
    const isModified = computed(() => !!description.value?.isModified);

    const queueSave = async () => {
        if (!isModified.value || !flow.value) {
            return;
        }

        const updateContent: types.StartProcessProcessElementMgtUpdateFlowStartPostData =
            {
                body: {
                    flow_id: flow.value.identity,
                },
            };

        if (description.value?.isModified) {
            updateContent.body.description = description.value.blob;
        }

        if (
            !updateContent.body.flow_id ||
            Object.keys(updateContent.body).length === 1
        ) {
            // Only flow_id is set, no need to update.
            return;
        }

        syncStore.queueSync({
            entityType: EntityType.FLOW,
            args: [updateContent],
        });

        if (description.value?.isModified) {
            description.value.persist();
        }
    };

    const reset = async () => {
        await description.value?.resetContent();
    };

    const { isEditing } = useEditManager();
    const throttledSave = throttle(queueSave, FILE_SYNC_THROTTLE_MS);
    registerFlush(() => throttledSave.flush());
    watch([isEditing, isModified], ([_, modified]) => {
        if (modified) {
            throttledSave();
        }
    });

    // One shared opts object: the notification guard and the landing-time
    // commit gates inside the update functions must agree on what "would
    // clobber local changes".
    const externalRefresh = {
        entityType: EntityType.FLOW,
        currentEntityId: () => flow.value?.identity,
        isDirty: () => isModified.value,
        refetch: () => updateFlow(true),
    };

    refreshOnCrossTabEntitySync({
        ...externalRefresh,
        enabled: () => !!stek.value,
    });

    const deleteFlow = async () => {
        if (!flow.value) {
            return;
        }
        if (!flow.value.is_archived) {
            throw Error("Can only delete archived flows");
        }

        await api.startProcessProcessElementMgtDeleteFlowStartPost({
            body: {
                flow_id: flow.value.identity,
            },
        });

        if (organization.value && stek.value && process.value) {
            await router.push(
                withThreadRevision({
                    name: "processPage",
                    params: {
                        orgName: organization.value.name,
                        stekName: stek.value.name,
                        processId: process.value.identity,
                    },
                })
            );
        }
    };

    return {
        flow,
        description,
        isModified,
        isFetching,
        reset,
        deleteFlow,
    };
});

export const useFlowManager = () => storeToRefs(useFlowStore());
